import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios, { axiosBuzzOne } from '../../../axios';
import { moduleName } from '../moduleInfo';
import {
  commonApprovalRoute,
  commonOneApprovalRoute,
  getApprovalListRoute,
  getEditorialStoryListRoute,
  kycApprovalRoute,
  reviewDetailsRoute,
  storyDetailsRoute,
  userDetailsRoute,
} from '../utils/apiRoutes';
import { API_BASE_BUZZONE } from '../../../environments';

export const getApprovalListAsync = createAsyncThunk(
  `${moduleName}/getApprovalListAsync`,
  async (payload) => {
    const { params } = payload;
    const response = await axiosBuzzOne({
      method: 'GET',
      url: getApprovalListRoute(),
      params: { ...params },
    });
    const data = response.data;
    return data;
  }
);

export const kycApprovalAsync = createAsyncThunk(
  `${moduleName}/kycApprovalAsync`,
  async (payload, { rejectWithValue }) => {
    try {
      const { _id, bodyData } = payload;
      const response = await axiosBuzzOne({
        method: 'PUT',
        url: kycApprovalRoute(_id),
        data: { ...bodyData },
      });
      const data = response.data;
      return data;
    } catch (error) {
      console.log({ error });
      return rejectWithValue(error.response.data);
    }
  }
);

export const getCommonOneApprovalAsync = createAsyncThunk(
  `${moduleName}/getCommonOneApprovalAsync`,
  async (payload) => {
    const { _id } = payload;
    const response = await axiosBuzzOne({
      method: 'GET',
      url: commonOneApprovalRoute(_id),
    });
    const data = response.data;
    return data;
  }
);

export const commonApprovalAsync = createAsyncThunk(
  `${moduleName}/commonApprovalAsync`,
  async (payload, { rejectWithValue }) => {
    try {
      const { _id, bodyData } = payload;
      const response = await axiosBuzzOne({
        method: 'PUT',
        url: commonApprovalRoute(_id),
        data: { ...bodyData },
      });
      const data = response.data;
      return data;
    } catch (error) {
      console.log({ error });
      return rejectWithValue(error.response.data);
    }
  }
);

export const getStoryDetailsAsync = createAsyncThunk(
  `${moduleName}/getStoryDetailsAsync`,
  async (payload, { rejectWithValue }) => {
    try {
      const { entityId } = payload;
      const response = await axiosBuzzOne({
        method: 'GET',
        url: storyDetailsRoute(entityId),
      });
      const data = response.data;
      return data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getReviewDetailsAsync = createAsyncThunk(
  `${moduleName}/getReviewDetailsAsync`,
  async (payload, { rejectWithValue }) => {
    try {
      const { entityId } = payload;
      const response = await axios({
        method: 'GET',
        url: reviewDetailsRoute(entityId),
      });
      const data = response.data;
      return data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getUserDetailsAsync = createAsyncThunk(
  `${moduleName}/getUserDetailsAsync`,
  async (payload, { rejectWithValue }) => {
    try {
      const { userId } = payload;
      const response = await axios({
        method: 'GET',
        url: userDetailsRoute(userId),
      });
      const data = response.data;
      return data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getEditorialStoryAsync = createAsyncThunk(
  `${moduleName}/getEditorialStoryAsync`,
  async (payload) => {
    const { params } = payload;
    const response = await axiosBuzzOne({
      method: 'GET',
      url: getEditorialStoryListRoute(),
      params: { ...params },
    });
    const data = response.data;
    return data;
  }
);

const createEditStoryAsync = (actionName, method) =>
  createAsyncThunk(`${moduleName}/${actionName}`, async (payload) => {
    // console.log({ payload });
    let url;
    if (actionName === 'updateStory')
      url = `${API_BASE_BUZZONE}/bcc/article/${payload._id}`;
    else url = `${API_BASE_BUZZONE}/bcc/article`;

    const response = await axios({
      method: method,
      url,
      data: payload,
    });
    const data = response.data;
    return data;
  });

export const createNewStoryAsync = createEditStoryAsync('createStory', 'POST');
export const updateStoryAsync = createEditStoryAsync('updateStory', 'PATCH');

const approvalSlice = createSlice({
  name: `${moduleName}/ManagementAppApproval`,
  initialState: {
    loading: false,
    userKycList: [],
    userKycCount: 0,
    schoolKycList: [],
    schoolKycCount: 0,
    commonApprovalList: [],
    commonApprovalCount: 0,
    commonOneApprovalDetails: null,
    storyDetails: null,
    reviewDetails: null,
    userDetails: null,
    editorialStory: [],
  },
  reducers: {
    emptyCommonApprovalState: (state) => {
      state.commonApprovalList = [];
      state.commonApprovalCount = 0;
    },
    emptyCommonOneApproval: (state) => {
      state.commonOneApprovalDetails = null;
    },
    emptyUserDetails: (state) => {
      state.userDetails = null;
    },
    emptyReviewDetails: (state) => {
      state.reviewDetails = null;
    },
  },
  extraReducers: {
    [getApprovalListAsync.pending]: (state) => {
      state.loading = true;
    },
    [getApprovalListAsync.fulfilled]: (state, action) => {
      state.loading = false;
      switch (action.meta.arg.type) {
        case 'userKyc':
          // console.log({ actionType: action.meta.arg.type });
          state.userKycList = action.payload.data;
          state.userKycCount = action.payload.count;
          break;

        case 'schoolKyc':
          // console.log({ actionType: action.meta.arg.type });
          state.schoolKycList = action.payload.data;
          state.schoolKycCount = action.payload.count;
          break;

        case 'common':
          // console.log({ actionType: action.meta.arg.type });
          state.commonApprovalList = action.payload.data;
          state.commonApprovalCount = action.payload.count;
          break;

        default:
          break;
      }
    },
    [getApprovalListAsync.rejected]: (state, action) => {
      state.loading = false;
      switch (action.meta.arg.type) {
        case 'userKyc':
          // console.log({ actionType: action.meta.arg.type });
          state.userKycList = [];
          state.userKycCount = 0;
          break;

        case 'schoolKyc':
          // console.log({ actionType: action.meta.arg.type });
          state.schoolKycList = [];
          state.schoolKycCount = 0;
          break;

        case 'common':
          // console.log({ actionType: action.meta.arg.type });
          state.commonApprovalList = [];
          state.commonApprovalCount = 0;
          break;

        default:
          break;
      }
    },
    [kycApprovalAsync.pending]: (state) => {
      state.loading = true;
    },
    [kycApprovalAsync.fulfilled]: (state, action) => {
      state.loading = false;
      state.userKycDetails = action.payload.data;
    },
    [kycApprovalAsync.rejected]: (state) => {
      state.loading = false;
    },
    [getCommonOneApprovalAsync.pending]: (state) => {
      state.loading = true;
    },
    [getCommonOneApprovalAsync.fulfilled]: (state, action) => {
      state.loading = false;
      state.commonOneApprovalDetails = action.payload.data;
    },
    [getCommonOneApprovalAsync.rejected]: (state) => {
      state.loading = false;
    },
    [commonApprovalAsync.pending]: (state) => {
      state.loading = true;
    },
    [commonApprovalAsync.fulfilled]: (state) => {
      state.loading = false;
    },
    [commonApprovalAsync.rejected]: (state) => {
      state.loading = false;
    },
    [getStoryDetailsAsync.pending]: (state) => {
      state.loading = true;
    },
    [getStoryDetailsAsync.fulfilled]: (state, action) => {
      state.loading = false;
      state.storyDetails = action.payload.data;
    },
    [getStoryDetailsAsync.rejected]: (state) => {
      state.loading = false;
    },
    [getReviewDetailsAsync.pending]: (state) => {
      state.loading = true;
    },
    [getReviewDetailsAsync.fulfilled]: (state, action) => {
      state.loading = false;
      state.reviewDetails = action.payload.data;
    },
    [getReviewDetailsAsync.rejected]: (state) => {
      state.loading = false;
    },
    [getUserDetailsAsync.pending]: (state) => {
      state.loading = true;
    },
    [getUserDetailsAsync.fulfilled]: (state, action) => {
      state.loading = false;
      state.userDetails = action.payload.data;
    },
    [getUserDetailsAsync.rejected]: (state) => {
      state.loading = false;
    },
    [getEditorialStoryAsync.pending]: (state) => {
      state.loading = true;
    },
    [getEditorialStoryAsync.fulfilled]: (state, action) => {
      state.loading = false;
      state.editorialStory = action.payload;
    },
    [getEditorialStoryAsync.rejected]: (state, action) => {
      state.loading = false;
    },
    [createNewStoryAsync.fulfilled]: (state) => {
      state.loading = false;
    },
    [createNewStoryAsync.pending]: (state) => {
      state.loading = true;
    },
    [createNewStoryAsync.rejected]: (state) => {
      state.loading = false;
    },
    [updateStoryAsync.fulfilled]: (state) => {
      state.loading = false;
    },
    [updateStoryAsync.pending]: (state) => {
      state.loading = true;
    },
    [updateStoryAsync.rejected]: (state) => {
      state.loading = false;
    },
  },
});

export const {
  emptyCommonApprovalState,
  emptyCommonOneApproval,
  emptyUserDetails,
  emptyReviewDetails,
} = approvalSlice.actions;

export default approvalSlice.reducer;
